<template>
  <div class="asc-mx-rec-info-wrapper">
    <asc-card class="asc-mx-rec-info-wrapper__card" flat>
      <asc-tab-body :value="tabIndex">
        <asc-tab-item>
          <v-row no-gutters>
            <v-col cols="7" sm="9">
              <p v-if="isEditPermissionInvestigate" class="mb-0">
                Fill out, review, and save all investigative info within this
                Investigate tab. Navigate to Mx Recommendation tab to review
                what airline customers see following submittal.
                <a :href="step1To" target="_blank" rel="noopener noreferrer"
                  >Visit Step 1 Tool
                  <v-icon size="14">mdi-open-in-new</v-icon></a
                >
                for advanced analytic support.
              </p>
              <p v-if="isReadOnlyPermissionInvestigate">
                Investigate shows all relevant analytic data.
              </p>
            </v-col>
          </v-row>
        </asc-tab-item>
        <asc-tab-item>
          <v-row class="pb-1">
            <v-col>
              <h1 class="asc-mxrec-recommendation--title">
                {{ getTitle }}
              </h1>
            </v-col>
            <v-col cols="2" sm="4" md="2">
              <template v-if="isCustomAlert">
                <asc-autocomplete-selector
                  v-model="customAlertMxRecstatusId"
                  label="Status"
                  class="mt-2 mb-2"
                  :sort-items="false"
                  :items="mxRecStatusItems"
                  :display-group-name="false"
                  no-array
                  :readonly="isReadOnlyPermissionInvestigate"
                ></asc-autocomplete-selector>
              </template>
              <template v-else>
                <asc-autocomplete-selector
                  v-model="getStatusId"
                  placeholder="Select a status"
                  label="Status"
                  class="mt-2 mb-2 asc-autocomplete-selector--status"
                  no-array
                  :items="getStatusOptions"
                  :display-group-name="false"
                  :locked-items="getLockedStatusOptions"
                  :sort-items="false"
                  :readonly="isStatusDisabled"
                />
              </template>
            </v-col>
            <v-col
              v-if="hasSubmit"
              cols="2"
              sm="4"
              md="2"
              class="d-flex padding-mxrec-submit justify-end flex-wrap mt-2 mb-2"
            >
              <asc-accent-button
                outline
                secondary
                custom-class="v-btn--has-bg"
                :button-text="getSubmitButtonText"
                :on-click="handleSubmit"
                :disabled="disableSubmit"
                width="146"
              ></asc-accent-button>
            </v-col>
          </v-row>
          <v-divider></v-divider>
          <v-row v-if="getSource && mxRecHeaderData" class="mt-3 mb-0">
            <v-col
              class="py-0"
              :sm="$vuetify.breakpoint.mdAndDown ? 5 : undefined"
              :md="isCustomAlert ? 2 : isChronicRepeaters ? 'auto' : undefined"
            >
              <asc-text-area-input
                label="Issue Source"
                :value="getSource"
                :readonly="true"
                hide-details
              />
            </v-col>
            <v-col
              class="py-0"
              :lg="isCustomAlert ? 2 : undefined"
              :sm="$vuetify.breakpoint.mdAndDown ? 3 : undefined"
            >
              <template v-if="isChronicRepeaters">
                <asc-autocomplete-selector
                  v-model="getSeverity"
                  label="Severity"
                  display-clear
                  no-array
                  :items="severityOptions"
                  :readonly="isReadOnlyPermissionInvestigate || lockEnabled"
                  :sort-items="false"
                  class="mt-1 mb-2 asc-autocomplete-selector--status"
                />
              </template>
              <template v-else>
                <asc-text-area-input
                  label="Severity"
                  :value="
                    isCustomAlert ? getCustomAlertSeverityLevel : severity
                  "
                  :readonly="true"
                  hide-details
                />
              </template>
            </v-col>
            <v-col
              class="py-0"
              :sm="$vuetify.breakpoint.mdAndDown ? 3 : undefined"
              :md="$vuetify.breakpoint.mdAndDown ? 3 : 2"
              :lg="isCustomAlert ? 2 : undefined"
            >
              <asc-text-area-input
                :label="isChronicRepeaters ? 'First Event Date' : 'Created'"
                :value="
                  isChronicRepeaters
                    ? `${createdDate} ${createdDateDays}`
                    : `${createdDate}\r\n${createdDateDays}`
                "
                :readonly="true"
                hide-details
              />
            </v-col>
            <v-col
              class="py-0"
              :lg="isCustomAlert ? 1 : undefined"
              :sm="$vuetify.breakpoint.mdAndDown ? 2 : undefined"
            >
              <asc-text-area-input
                label="Tail"
                :value="mxRecHeaderData.tailname"
                :readonly="true"
                hide-details
              />
            </v-col>
            <v-col
              class="py-0"
              :lg="isCustomAlert ? 1 : undefined"
              :sm="$vuetify.breakpoint.mdAndDown ? 2 : undefined"
            >
              <asc-text-area-input
                label="Platform"
                :value="mxRecHeaderData.platform"
                :readonly="true"
                hide-details
              />
            </v-col>
            <v-col
              class="py-0"
              :lg="isCustomAlert ? 1 : undefined"
              :sm="$vuetify.breakpoint.mdAndDown ? 2 : undefined"
            >
              <asc-text-area-input
                label="ATA"
                :value="mxRecHeaderData.ataChapter"
                :readonly="true"
                hide-details
              />
            </v-col>
            <v-col
              v-if="isCustomAlert"
              class="py-0"
              :sm="$vuetify.breakpoint.mdAndDown ? 6 : undefined"
              :lg="isCustomAlert ? 3 : undefined"
              md="5"
            >
              <asc-text-area-input
                label="Analytic Logic"
                :value="getAnalyticModuleName"
                :readonly="true"
                :rows="3"
                hide-details
              />
            </v-col>
            <template v-else>
              <template v-if="!isChronicRepeaters">
                <v-col
                  class="py-0"
                  :sm="$vuetify.breakpoint.mdAndDown ? 2 : undefined"
                >
                  <asc-text-area-input
                    label="System"
                    :value="mxRecHeaderData.system"
                    :readonly="true"
                    hide-details
                  />
                </v-col>
                <v-col
                  class="py-0"
                  :sm="$vuetify.breakpoint.mdAndDown ? 2 : undefined"
                  :md="
                    mxRecHeaderData.component &&
                    mxRecHeaderData.component.length >= 15
                      ? 2
                      : undefined
                  "
                >
                  <asc-text-area-input
                    label="Component"
                    :value="mxRecHeaderData.component"
                    :readonly="true"
                    hide-details
                  />
                </v-col>
              </template>
            </template>
          </v-row>
        </asc-tab-item>
      </asc-tab-body>
    </asc-card>
  </div>
</template>

<script>
import AscAccentButton from '@/components/atoms/AscAccentButton'
import AscCard from '@/components/atoms/AscCard'
import AscTabBody from '@/components/atoms/AscTabBody'
import AscTabItem from '@/components/atoms/AscTabItem'
import AscTextAreaInput from '@/components/atoms/AscTextareaInput'
import {
  AscMxRecPropsMixin,
  AscMxRecPermissionMixin,
} from '@/components/mixins'
import AscAutocompleteSelector from '../molecules/AscAutocompleteSelector.vue'
import { COLLINS_PHM_TIER_3 } from '@/utilities/appConsts'
import moment from 'moment'
import VClamp from 'vue-clamp'
import { STATUS_TITLES } from '@/utilities/appConsts'
export default {
  name: 'AscMxRecInfoWrapper',

  components: {
    AscCard,
    AscAccentButton,
    AscTabBody,
    AscTabItem,
    AscTextAreaInput,
    AscAutocompleteSelector,
    VClamp,
  },

  mixins: [AscMxRecPropsMixin, AscMxRecPermissionMixin],

  props: {
    disableSubmit: {
      type: Boolean,
      default: true,
    },
    issueSource: {
      type: Boolean,
      default: false,
    },
    aircraftDetails: {
      type: Object,
      default: () => {},
    },
    statusOptions: {
      type: Array,
      default: () => [],
    },
    lockedItems: {
      type: Array,
      default: () => [],
    },
    customAlertMxRecStatusOptions: {
      type: Array,
      default: () => [],
    },
    customAlertOwnerData: {
      type: Array,
      default: () => [],
    },
    lockEnabled: {
      type: Boolean,
      default: false,
    },
    severityOptions: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      step1To:
        'https://collins-ds-reports.azurewebsites.net/Reporting/html/index.html',
      severityId: this.aircraftDetails?.severityId,
    }
  },

  computed: {
    getATAChapters() {
      const ataChapter = this.mxRecHeaderData.ataChapter.split(',')
      if (ataChapter.length) {
        if (ataChapter.length === 1) {
          return ataChapter[0]
        } else {
          return `${ataChapter.join(', ')}`
        }
      }

      return 'N/A'
    },
    getTitle() {
      if (this.isChronicRepeaters) {
        return this.mxRecHeaderData
          ? `REPEATER ON ATA ${this.getATAChapters} ON TAIL ${this.mxRecHeaderData.tailname}`
          : ''
      } else {
        return this.mxRecHeaderData.title
      }
    },
    mxRecStatusItems() {
      const customAlertMxRecstatus = this.customAlertMxRecStatusOptions.map(
        item => {
          return {
            text: item.name,
            value: item.id,
            groupName: item.isTerminal ? 'closed' : 'airline',
          }
        },
      )
      return customAlertMxRecstatus
    },
    customAlertMxRecstatusId: {
      get() {
        return this.aircraftDetails?.status ? this.aircraftDetails.status.id : 0
      },
      set(val) {
        this.handleStatusChange(val)
      },
    },
    getAnalyticModuleName() {
      return this.aircraftDetails?.analyticModuleName
    },
    getCustomAlertOwnerdata() {
      const name = this.customAlertOwnerData.filter(
        item => item.userId == this.aircraftDetails?.ownerUserId,
      )

      return name.length > 0 ? `(${name[0].firstName} ${name[0].lastName})` : ''
    },
    hasSubmit() {
      return (
        (this.isEditPermissionInvestigate ||
          this.isReadOnlyPermissionInvestigate) &&
        this.airlineTierTypeId !== COLLINS_PHM_TIER_3 &&
        !this.isCustomAlert &&
        !this.isChronicRepeaters
      )
    },
    getSubmitButtonText() {
      let text = 'Submit Mx Rec'
      if (!this.isPreSubmittal && !this.disableSubmit) {
        text = 'Submit Mx Rec'
      } else if (!this.isPreSubmittal && this.disableSubmit) {
        text = 'Mx Rec Submitted'
      } else if (this.isPreSubmittal && this.disableSubmit) {
        text = 'Submit Mx Rec'
      }
      return text
    },
    getLockedStatusOptions() {
      // since we are incrementing all the status values above (so you can't have an item with 0)
      // be sure to increment the status values of any locked options
      return this.lockedItems.map(value => value + 1)
    },
    mxRecHeaderData() {
      return {
        title: this.aircraftDetails?.title,
        tailname: this.aircraftDetails?.tail?.name,
        status: this.aircraftDetails?.status,
        createdDate: this.aircraftDetails?.createdDate,
        component:
          this.aircraftDetails?.component?.name &&
          this.aircraftDetails?.positionName
            ? this.aircraftDetails?.component?.name +
              ` (${this.aircraftDetails?.positionName})`
            : this.aircraftDetails?.component?.name,
        system: this.aircraftDetails?.system?.name,
        platform: this.aircraftDetails?.masterModel?.name,
        ataChapter: this.aircraftDetails?.ataChapter,
      }
    },
    severity() {
      let severity = ''
      if (this.aircraftDetails?.severityId === 1) severity = '1 - Critical'
      if (this.aircraftDetails?.severityId === 2) severity = '2 - High'
      if (this.aircraftDetails?.severityId === 3) severity = '3 - Medium'
      if (this.aircraftDetails?.severityId === 4) severity = '4 - Low'
      return severity
    },
    getCustomAlertSeverityLevel() {
      return STATUS_TITLES[this.aircraftDetails?.severityLevel]
    },

    getSource() {
      return !this.issueSource
        ? ' '
        : this.isManuallyCreated
        ? 'Manually Created'
        : this.isCustomAlert
        ? 'Custom Analytic ' + this.getCustomAlertOwnerdata
        : this.isChronicRepeaters
        ? this.aircraftDetails.source.name
        : 'PHM Alert'
    },
    createdDateDays() {
      // Add user's name once it is added to mx issue data

      var dateCreated = this.aircraftDetails?.createdDate
      if (dateCreated) {
        const currentDate = new Date()
        const previousDate = new Date(dateCreated)
        const timeDifference = currentDate.getTime() - previousDate.getTime()
        const daysDifference = Math.floor(
          timeDifference / (1000 * 60 * 60 * 24),
        )

        return daysDifference ? `(${daysDifference} Days ago)` : ''
      } else {
        return ''
      }
    },
    createdDate() {
      const created = this.aircraftDetails?.createdDate
        ? `${moment(this.aircraftDetails?.createdDate).format('DD MMM YYYY')}`
        : ''
      return `${created}`
    },
    getStatusOptions: {
      get() {
        const statusOptions = this.isChronicRepeaters
          ? this.statusOptions.filter(item => item.isChronicRepeater)
          : this.statusOptions

        return statusOptions
          .map(statusObj => ({ ...statusObj, value: statusObj.value + 1 }))
          .sort((a, b) => a.sortOrder - b.sortOrder)
      },
    },

    isStatusDisabled() {
      if (
        this.isCustomAlert ||
        this.isReadOnlyPermissionInvestigate ||
        this.lockEnabled
      )
        return true
      else {
        return false
      }
    },
    getStatusId: {
      get() {
        const whichIndex = this.statusOptions.filter(statusOptionObj => {
          return statusOptionObj.id === this.aircraftDetails.status.id
        })
        if (whichIndex.length > 0) {
          // need to return the value incremented by 1 here since we had
          // to increment all the values of the statuses
          return whichIndex[0].value + 1
        } else {
          return null
        }
      },
      set(val) {
        const whichIndex = this.statusOptions.filter(statusOptionObj => {
          return statusOptionObj.value + 1 === val
        })
        if (whichIndex.length > 0) {
          this.handleStatusChange(whichIndex[0].id)
        } else {
          this.handleStatusChange(null)
        }
      },
    },

    getSeverity: {
      get() {
        return this.aircraftDetails?.severityId
      },
      set(val) {
        this.severityId = val
        this.$emit('updateSeverity', val)
      },
    },
  },

  mounted() {
    this.onResize()
  },

  methods: {
    onResize() {
      this.windowSize = { x: window.innerWidth, y: window.innerHeight }
    },
    handleSubmit() {
      this.$emit('changeInvestigation', 'submit')
    },
    handleStatusChange(val) {
      this.$emit('updateStatus', val)
    },
  },
}
</script>

<style lang="scss">
.asc-mx-rec-info-wrapper {
  &__card {
    .asc-card__text {
      .asc-tab__content--primary {
        a {
          color: $accent-700;
          text-decoration: none;

          .v-icon {
            color: inherit;
          }
        }
      }
    }

    .padding-mxrec-submit {
      padding-bottom: 80px !important;
    }

    .asc-autocomplete-selector--status {
      .asc-autocomplete-selector-options--container
        .asc-selector--options-container
        .asc-selector--available-list-container {
        max-height: 355px;
      }
    }
    .asc-autocomplete-selector--severity {
      padding-top: 0px;
      .asc-autocomplete-selector-options--container
        .asc-selector--options-container
        .asc-selector--available-list-container {
        max-height: 355px;
      }
    }
  }
}
</style>
